// Team Calendar — manager view over the unified Workforce Calendar feed.
// Defaults to scope=direct (IMMEDIATE reports); 'team' (the recursive
// subtree) is an explicit choice, because for anyone near the top of an org
// chart the subtree IS the whole company. Shows approved team leave
// (privacy-safe titles — sick leave shows
// as "Unavailable"), holidays, team assignments and open attendance
// exceptions. Names are resolved server-side by the gateway; the month range
// follows navigation (the old page was pinned to today→+90 days).
import { weekdayLabels, weekdayOffset } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, Clock, Loader2, Users } from 'lucide-react';
import {
  CALENDAR_TYPE_META, eventsOnDay,
  type CalendarEventType, type WorkforceCalendarEvent,
} from '@/lib/calendarApi';
import { useCalendarFeed } from '@/features/calendar/useCalendarFeed';
import { loadHiddenTypes, saveHiddenTypes, loadTeamScope, saveTeamScope, type TeamCalendarScope } from '@/features/calendar/calendarPrefs';
import { CalendarFilterLegend } from '@/features/calendar/CalendarFilterLegend';
import { EventDetailSheet } from '@/features/calendar/EventDetailSheet';
import { DayEventsSheet } from '@/features/calendar/DayEventsSheet';

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
// LOCAL calendar date, computed on demand — the old module-scope constant was
// (a) frozen at bundle load and (b) the UTC date, so "today" was wrong for the
// whole session west of UTC and after any midnight (timezone program 2026-08).
const localTodayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PREFS_KEY = 'team';

export const TeamCalendarPage: React.FC = () => {
  const { t } = useTranslation('timeoff');
  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState<WorkforceCalendarEvent | null>(null);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(() => loadHiddenTypes(PREFS_KEY));
  const [dayDetail, setDayDetail] = useState<string | null>(null);
  // ⚠️ 'direct' by default: immediate reports only. 'team' is the recursive
  // subtree, which for anyone near the top of the org chart is the whole
  // company — that is what this page used to show unconditionally.
  const [scope, setScope] = useState<TeamCalendarScope>(() => loadTeamScope());
  const { events, warnings, loading, error, reload } = useCalendarFeed(scope, cursor);

  useEffect(() => { saveHiddenTypes(PREFS_KEY, hiddenTypes); }, [hiddenTypes]);
  useEffect(() => { saveTeamScope(scope); }, [scope]);
  const toggleType = (tp: CalendarEventType) =>
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(tp)) next.delete(tp); else next.add(tp);
      return next;
    });

  const availableTypes = useMemo(() => {
    const present = new Set(events.map((e) => e.type));
    return (Object.keys(CALENDAR_TYPE_META) as CalendarEventType[]).filter((tp) => present.has(tp));
  }, [events]);

  const visible = useMemo(() => events.filter((e) => !hiddenTypes.has(e.type)), [events, hiddenTypes]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = weekdayOffset(new Date(year, month, 1).getDay());
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const goMonth = (delta: number) => setCursor(new Date(year, month + delta, 1));

  const leaveEvents = visible.filter((e) => e.type === 'leave');
  // "Out today" counts people ACTUALLY out — approved only, never pending.
  const outToday = leaveEvents.filter((e) => {
    const start = e.startAt.slice(0, 10);
    const end = (e.endAt || e.startAt).slice(0, 10);
    return e.status === 'approved' && start <= localTodayIso() && end >= localTodayIso();
  }).length;

  const upcoming = useMemo(
    () => leaveEvents
      .filter((e) => (e.endAt || e.startAt).slice(0, 10) >= localTodayIso())
      .sort((a, b) => a.startAt.localeCompare(b.startAt))
      .slice(0, 6),
    [leaveEvents],
  );

  const fmtShort = (d: string) => new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal">{t('team_calendar.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('team_calendar.subtitle')}</p>
      </div>

      {/* Scope. A PREFERENCE, not a permission — the server re-decides what
          this caller may see on every request, so switching here can never
          widen access, only ask for less. */}
      <div className="flex items-center gap-1 rounded-[10px] border border-border p-1 w-fit" role="group"
           aria-label={t('team_calendar.scope_label', { defaultValue: 'Which team' })}>
        {(['direct', 'team'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            aria-pressed={scope === s}
            className={`px-3 py-1.5 text-sm rounded-[8px] transition-colors ${
              scope === s
                ? 'bg-orange-500 text-white'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {s === 'direct'
              ? t('team_calendar.scope_direct', { defaultValue: 'Direct team' })
              : t('team_calendar.scope_org', { defaultValue: 'Whole organisation' })}
          </button>
        ))}
      </div>

      {(warnings.length > 0 || error) && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/[0.08] px-3 py-2 text-sm text-amber-800 dark:text-amber-300" role="status">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            {error || warnings.join(' ')}
            <button type="button" onClick={reload} className="ml-2 underline font-medium">{t('mycalendar.retry', 'Retry')}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <Calendar className="w-5 h-5" /> {MONTHS[month]} {year}
                </CardTitle>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => goMonth(-1)} aria-label={t('team_calendar.previous_month')}
                    className="h-8 w-8 rounded-lg border border-border bg-card text-foreground hover:bg-muted hover:border-orange-300 inline-flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setCursor(new Date())}
                    className="h-8 px-3 rounded-lg border border-border bg-card text-foreground hover:bg-muted hover:border-orange-300 text-sm font-medium">{t('team_calendar.today')}</button>
                  <button type="button" onClick={() => goMonth(1)} aria-label={t('team_calendar.next_month')}
                    className="h-8 w-8 rounded-lg border border-border bg-card text-foreground hover:bg-muted hover:border-orange-300 inline-flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {loading ? (
                <div className="py-16 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('team_calendar.loading')}</div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {weekdayLabels().map((d) => (
                    <div key={d} className="p-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">{d}</div>
                  ))}
                  {Array.from({ length: firstWeekday }, (_, i) => <div key={`b${i}`} className="min-h-[84px] rounded-lg bg-muted/40" />)}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const dIso = iso(year, month, day);
                    const dayEvents = eventsOnDay(visible, dIso);
                    const hasHoliday = dayEvents.some((e) => e.type === 'holiday');
                    const isToday = dIso === localTodayIso();
                    return (
                      <div key={day} className={`min-h-[84px] rounded-lg border p-1.5 ${isToday ? 'border-orange-400 bg-orange-50/50 dark:bg-orange-500/[0.08]' : hasHoliday ? 'border-indigo-200 bg-indigo-50/40 dark:border-indigo-500/30 dark:bg-indigo-500/[0.08]' : 'border-border bg-card'}`}>
                        <button
                          type="button"
                          onClick={() => setDayDetail(dIso)}
                          aria-label={`Show all events on day ${day}`}
                          className={`text-xs font-medium mb-1 rounded px-1 -mx-1 hover:bg-muted ${isToday ? 'text-orange-600' : 'text-foreground'}`}
                        >
                          {day}
                        </button>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((e) => (
                            <button
                              key={e.id}
                              type="button"
                              onClick={() => setSelected(e)}
                              className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate ${CALENDAR_TYPE_META[e.type].chip}${e.status === 'pending' ? ' opacity-60 border border-dashed border-current' : ''}`}
                              title={`${CALENDAR_TYPE_META[e.type].label} · ${e.title}${e.status === 'pending' ? ' · Pending' : ''}`}
                            >
                              {e.type === 'leave' && e.personName ? e.personName.split(' ')[0] : e.title}
                            </button>
                          ))}
                          {dayEvents.length > 3 && (
                            <button
                              type="button"
                              onClick={() => setDayDetail(dIso)}
                              className="text-[10px] text-muted-foreground hover:text-orange-600 px-1 font-medium"
                            >
                              +{dayEvents.length - 3} more
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardContent className="pt-4 pb-4">
              <CalendarFilterLegend availableTypes={availableTypes} hiddenTypes={hiddenTypes} onToggle={toggleType} />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl"><CardTitle className="flex items-center gap-2 text-orange-700"><Users className="w-5 h-5" /> {t('team_calendar.out_today')}</CardTitle></CardHeader>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-foreground">{outToday}</div>
              <p className="text-sm text-muted-foreground">{outToday === 1 ? t('team_calendar.person_off_today') : t('team_calendar.people_off_today')}</p>
            </CardContent>
          </Card>

          <Card className="border-orange-100 dark:border-orange-500/20">
            <CardHeader className="rounded-t-xl"><CardTitle className="flex items-center gap-2 text-orange-700"><Clock className="w-5 h-5" /> {t('team_calendar.upcoming')}</CardTitle></CardHeader>
            <CardContent className="pt-6">
              {loading ? (
                <div className="py-6 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : upcoming.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">{t('team_calendar.no_upcoming')}</div>
              ) : (
                <div className="space-y-3">
                  {upcoming.map((e) => (
                    <button key={e.id} type="button" onClick={() => setSelected(e)} className="w-full flex items-start gap-3 text-left">
                      <span className={`mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${CALENDAR_TYPE_META.leave.chip}`}>
                        {t('team_calendar.type_time_off')}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-foreground truncate">{e.personName || e.title}</span>
                        <span className="block text-xs text-muted-foreground">
                          {fmtShort(e.startAt)}
                          {e.endAt && e.endAt.slice(0, 10) !== e.startAt.slice(0, 10) && ` – ${fmtShort(e.endAt)}`}
                          {e.status === 'pending' && ` · ${t('team_calendar.pending', 'Pending')}`}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <DayEventsSheet
        dayIso={dayDetail}
        events={dayDetail ? eventsOnDay(visible, dayDetail) : []}
        onClose={() => setDayDetail(null)}
        onSelect={(ev) => { setDayDetail(null); setSelected(ev); }}
      />
      <EventDetailSheet event={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default TeamCalendarPage;
